import * as Y from 'yjs';
import { nanoid } from 'nanoid';
import { withLocalTransaction } from '../transact';
import { yElements, yLines } from '../schema';
import { trackCanvasEvent } from '../../analytics';

function makeYElement(el) {
    const yEl = new Y.Map();
    yEl.set('id',     el.id);
    yEl.set('type',   el.type);
    yEl.set('x',      el.x ?? 0);
    yEl.set('y',      el.y ?? 0);
    yEl.set('width',  el.width  ?? 0);
    yEl.set('height', el.height ?? 0);
    yEl.set('areaId', el.areaId ?? null);

    if (el.type === 'table') {
        if (el.name        !== undefined) yEl.set('name',        el.name);
        if (el.description !== undefined) yEl.set('description', el.description);
        if (el.color       !== undefined) yEl.set('color',       el.color);
        if (el.border      !== undefined) yEl.set('border',      el.border);
        yEl.set('columns', new Y.Map());
        yEl.set('columnsOrder', new Y.Array());
    } else if (el.type === 'note') {
        if (el.name        !== undefined) yEl.set('name',        el.name);
        if (el.description !== undefined) yEl.set('description', el.description);
        if (el.color       !== undefined) yEl.set('color',       el.color);
        if (el.border      !== undefined) yEl.set('border',      el.border);
        if (el.text        !== undefined) yEl.set('text',        el.text);   // NEW
        yEl.set('alignmentHorizontal', el.alignmentHorizontal ?? 'left');
        yEl.set('alignmentVertical',   el.alignmentVertical   ?? 'top');
        yEl.set('isFontMonospace',     el.isFontMonospace     ?? false);
        yEl.set('isBold',          el.isBold          ?? false);
        yEl.set('isItalic',        el.isItalic        ?? false);
        yEl.set('isUnderline',     el.isUnderline     ?? false);
        yEl.set('isStrikethrough', el.isStrikethrough ?? false);
    } else if (el.type === 'pen-segment') {
        if (el.color    !== undefined) yEl.set('color',    el.color);
        if (el.width    !== undefined) yEl.set('width',    el.width);
        if (el.points   !== undefined) yEl.set('points',   el.points);
        // `path` is intentionally NOT persisted — it's rebuilt from `points` when a
        // pen-segment is loaded into the canvas (points are the source of truth).
        if (el.newPenId !== undefined) yEl.set('newPenId', el.newPenId);
    }

    return yEl;
}

export function addElement(doc, el) {
    withLocalTransaction(doc, () => {
        yElements(doc).set(el.id, makeYElement(el));
    });
    trackCanvasEvent(`app_canvas_${el.type || 'element'}_add`);
}

export function deleteElements(doc, ids) {
    const idList = toIdList(ids);
    if (!idList.length) return;
    const idSet = new Set(idList);
    withLocalTransaction(doc, () => {
        const lines = yLines(doc);
        for (const lineId of [...lines.keys()]) {
            const line = lines.get(lineId);
            // Line endpoints use the source/target* field names (with from/to* kept
            // as a legacy fallback). Without this, a deleted table's lines were left
            // orphaned, later crashing the canvas when their path couldn't be built.
            const fromId = line.get('sourceElementId') ?? line.get('fromElementId');
            const toId   = line.get('targetElementId') ?? line.get('toElementId');
            if (idSet.has(fromId) || idSet.has(toId)) {
                lines.delete(lineId);
            }
        }
        const elements = yElements(doc);
        for (const id of idList) elements.delete(id);
    });
    trackCanvasEvent('app_canvas_element_delete', { count: idList.length });
}

export function moveElements(doc, moves) {
    if (!moves?.length) return;
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);
        for (const { id, x, y } of moves) {
            const yEl = elements.get(id);
            if (!yEl) continue;
            if (x !== undefined) yEl.set('x', x);
            if (y !== undefined) yEl.set('y', y);
        }
    });
}

export function resizeElements(doc, sizes) {
    if (!sizes?.length) return;
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);
        for (const { id, width, height } of sizes) {
            const yEl = elements.get(id);
            if (!yEl) continue;
            if (width  !== undefined) yEl.set('width',  width);
            if (height !== undefined) yEl.set('height', height);
        }
    });
}

// Combines move + resize in one transaction so remote peers see a single atomic update.
export function moveAndResizeElements(doc, updates) {
    if (!updates?.length) return;
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);
        for (const { id, x, y, width, height } of updates) {
            const yEl = elements.get(id);
            if (!yEl) continue;
            if (x      !== undefined) yEl.set('x',      x);
            if (y      !== undefined) yEl.set('y',      y);
            if (width  !== undefined) yEl.set('width',  width);
            if (height !== undefined) yEl.set('height', height);
        }
    });
}

export function autoResizeElements(doc, sizes) {
    return resizeElements(doc, sizes);
}

// `ids` accepts either an array of element ids or a legacy id→element map
// (the context-menu style controls still pass the object shape).
function toIdList(ids) {
    return Array.isArray(ids) ? ids : Object.keys(ids ?? {});
}

export function setElementColor(doc, ids, color) {
    const idList = toIdList(ids);
    if (!idList.length) return;
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);
        for (const id of idList) {
            const yEl = elements.get(id);
            if (yEl) yEl.set('color', color);
        }
    });
    trackCanvasEvent('app_canvas_element_color_change');
}

export function setElementBorder(doc, ids, border) {
    const idList = toIdList(ids);
    if (!idList.length) return;
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);
        for (const id of idList) {
            const yEl = elements.get(id);
            if (yEl) yEl.set('border', border);
        }
    });
    trackCanvasEvent('app_canvas_element_border_change');
}

export function setElementDescription(doc, id, description) {
    withLocalTransaction(doc, () => {
        const yEl = yElements(doc).get(id);
        if (yEl) yEl.set('description', description);
    });
    trackCanvasEvent('app_canvas_element_description_add');
}

export function setElementName(doc, id, name) {
    withLocalTransaction(doc, () => {
        const yEl = yElements(doc).get(id);
        if (yEl) yEl.set('name', name);
    });
}

export function setNoteAlignment(doc, id, { horizontal, vertical }) {
    withLocalTransaction(doc, () => {
        const yEl = yElements(doc).get(id);
        if (!yEl) return;
        if (horizontal !== undefined) yEl.set('alignmentHorizontal', horizontal);
        if (vertical   !== undefined) yEl.set('alignmentVertical',   vertical);
    });
}

export function setNoteFontMonospace(doc, id) {
    withLocalTransaction(doc, () => {
        const yEl = yElements(doc).get(id);
        if (!yEl) return;
        yEl.set('isFontMonospace', !yEl.get('isFontMonospace'));
    });
}

export function setNoteText(doc, id, text) {
    withLocalTransaction(doc, () => {
        const yEl = yElements(doc).get(id);
        if (yEl) yEl.set('text', text);
    });
}

export function setNewAreaIdForElement(doc, id, areaId) {
    withLocalTransaction(doc, () => {
        const yEl = yElements(doc).get(id);
        if (yEl) yEl.set('areaId', areaId);
    });
}

function cloneYElement(yEl, { offsetX = 0, offsetY = 0 }) {
    const newId = nanoid();
    const cloned = new Y.Map();

    // Build old→new column ID map up front so columnsOrder can be remapped correctly.
    const columnIdMap = new Map();
    const columnsYMap = yEl.get('columns');
    if (columnsYMap) {
        columnsYMap.forEach((_, colKey) => columnIdMap.set(colKey, nanoid()));
    }

    yEl.forEach((value, key) => {
        if (key === 'id') {
            cloned.set('id', newId);
        } else if (key === 'x') {
            cloned.set('x', value + offsetX);
        } else if (key === 'y') {
            cloned.set('y', value + offsetY);
        } else if (key === 'columns') {
            const newCols = new Y.Map();
            value.forEach((col, colKey) => {
                const newColId = columnIdMap.get(colKey);
                const newCol = new Y.Map();
                col.forEach((v, k) => {
                    if (k === 'id') {
                        newCol.set('id', newColId);
                    } else if (k === 'isRelated') {
                        // isRelated is derived from line connections — the duplicate has none
                    } else {
                        newCol.set(k, v);
                    }
                });
                newCols.set(newColId, newCol);
            });
            cloned.set('columns', newCols);
        } else if (key === 'columnsOrder') {
            const newOrder = new Y.Array();
            newOrder.insert(0, value.toArray().map(oldId => columnIdMap.get(oldId) ?? oldId));
            cloned.set('columnsOrder', newOrder);
        } else {
            cloned.set(key, value);
        }
    });
    return { id: newId, yMap: cloned, columnIdMap };
}

// Clones every line whose BOTH endpoints were duplicated, remapping element + column
// ids onto the copies. Lines crossing the selection boundary (one duplicated endpoint,
// one not) are intentionally skipped. Must run inside an existing transaction.
function cloneLinesBetweenDuplicated(doc, elementIdMap, columnIdMap) {
    const ELEMENT_ID_KEYS = new Set([
        'sourceElementId', 'fromElementId', 'targetElementId', 'toElementId',
    ]);
    const COLUMN_ID_KEYS = new Set([
        'sourceColumnId', 'fromColumnId', 'fromInsideElementId',
        'targetColumnId', 'toColumnId', 'toInsideElementId',
    ]);
    const newLineIds = [];
    const lines = yLines(doc);
    for (const lineId of [...lines.keys()]) {
        const line = lines.get(lineId);
        const srcEl = line.get('sourceElementId') ?? line.get('fromElementId');
        const tgtEl = line.get('targetElementId') ?? line.get('toElementId');
        // Only duplicate a line if both of its endpoints were duplicated.
        if (!elementIdMap.has(srcEl) || !elementIdMap.has(tgtEl)) continue;

        const newLineId = nanoid();
        const newLine = new Y.Map();
        line.forEach((value, key) => {
            if (ELEMENT_ID_KEYS.has(key)) {
                newLine.set(key, elementIdMap.get(value) ?? value);
            } else if (COLUMN_ID_KEYS.has(key)) {
                newLine.set(key, columnIdMap.get(value) ?? value);
            } else {
                newLine.set(key, value);
            }
        });
        newLine.set('id', newLineId);
        lines.set(newLineId, newLine);
        newLineIds.push(newLineId);
    }
    return newLineIds;
}

export function duplicateElements(doc, ids, { offsetX = 50, offsetY = 50 } = {}) {
    const newIds = [];
    const idList = toIdList(ids);
    if (!idList.length) return newIds;
    const elementIdMap = new Map(); // old element id → new element id
    const columnIdMap = new Map();  // old column id → new column id
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);
        for (const id of idList) {
            const yEl = elements.get(id);
            if (!yEl) continue;
            const { id: newId, yMap, columnIdMap: colMap } = cloneYElement(yEl, { offsetX, offsetY });
            elements.set(newId, yMap);
            newIds.push(newId);
            elementIdMap.set(id, newId);
            for (const [oldCol, newCol] of colMap) columnIdMap.set(oldCol, newCol);
        }
        // Re-create lines that connected two duplicated elements.
        cloneLinesBetweenDuplicated(doc, elementIdMap, columnIdMap);
    });
    if (newIds.length) trackCanvasEvent('app_canvas_element_duplicate', { count: newIds.length });
    return newIds;
}

// Duplicates a group selection (areas + their nested elements) in one transaction.
// Areas are cloned first, then elements are cloned and any element that was nested in
// a duplicated area is re-parented onto the *copied* area (so the new area keeps its
// nested elements, mirroring the original). Returns the new ids by kind.
export function duplicateGroup(doc, { elementIds = [], areaIds = [] } = {}, { offsetX = 50, offsetY = 50 } = {}) {
    const elList = toIdList(elementIds);
    const arList = toIdList(areaIds);
    const newElementIds = [];
    const newAreaIds = [];
    if (!elList.length && !arList.length) return { newElementIds, newAreaIds };

    const areaIdMap = new Map();    // old area id → new area id
    const elementIdMap = new Map(); // old element id → new element id
    const columnIdMap = new Map();  // old column id → new column id
    withLocalTransaction(doc, () => {
        const elements = yElements(doc);

        // 1. Clone areas first, recording the old→new id mapping.
        for (const areaId of arList) {
            const yArea = elements.get(areaId);
            if (!yArea) continue;
            const newId = nanoid();
            const dup = new Y.Map();
            yArea.forEach((value, key) => {
                if (key === 'id') dup.set('id', newId);
                else if (key === 'x') dup.set('x', value + offsetX);
                else if (key === 'y') dup.set('y', value + offsetY);
                else dup.set(key, value);
            });
            elements.set(newId, dup);
            areaIdMap.set(areaId, newId);
            newAreaIds.push(newId);
        }

        // 2. Clone elements, re-parenting any nested in a duplicated area.
        for (const id of elList) {
            const yEl = elements.get(id);
            if (!yEl) continue;
            const { id: newId, yMap, columnIdMap: colMap } = cloneYElement(yEl, { offsetX, offsetY });
            const oldAreaId = yEl.get('areaId');
            if (oldAreaId != null && areaIdMap.has(oldAreaId)) {
                yMap.set('areaId', areaIdMap.get(oldAreaId));
            }
            elements.set(newId, yMap);
            newElementIds.push(newId);
            elementIdMap.set(id, newId);
            for (const [oldCol, newCol] of colMap) columnIdMap.set(oldCol, newCol);
        }

        // 3. Re-create lines that connected two duplicated elements.
        cloneLinesBetweenDuplicated(doc, elementIdMap, columnIdMap);
    });

    const total = newElementIds.length + newAreaIds.length;
    if (total) trackCanvasEvent('app_canvas_element_duplicate', { count: total });
    return { newElementIds, newAreaIds };
}
